import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadPdf, downloadExcel } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileDown, FileSpreadsheet, Save, Loader2 } from "lucide-react";

const K = [["ca", "CA HT"], ["tva", "TVA"], ["timbre", "Timbre"]];
const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const SEL = "h-9 rounded-md border bg-background px-2 text-sm";
const SEUIL = 1;
const cls = (e) => (e === null ? "text-muted-foreground" : Math.abs(e) >= SEUIL ? "text-red-600 font-semibold" : "text-emerald-600");

const EcartRow = ({ r, editable, onSave }) => {
  const [v, setV] = useState(() => Object.fromEntries(K.map(([k]) => [k, r[`${k}_declare`] ?? ""])));
  return (
    <tr data-testid={`ecart-row-${r.mois}`}>
      <td className="py-1.5 pr-2 font-medium">{r.label}</td>
      {K.map(([k]) => [
        <td key={`${k}f`} className="py-1.5 pr-2 tabular text-right">{fmtDA(r[`${k}_facture`])}</td>,
        <td key={`${k}d`} className="py-1.5 pr-2">
          {editable ? <Input type="number" step="0.01" className="h-8 w-32 text-right" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`ecart-input-${k}-${r.mois}`} />
            : <span className="tabular">{r[`${k}_declare`] === null ? "—" : fmtDA(r[`${k}_declare`])}</span>}
        </td>,
        <td key={`${k}e`} className={`py-1.5 pr-2 tabular text-right ${cls(r[`${k}_ecart`])}`}>{r[`${k}_ecart`] === null ? "—" : fmtDA(r[`${k}_ecart`])}</td>,
      ])}
      <td className="py-1.5 text-right">{editable && <Button size="sm" variant="ghost" onClick={() => onSave(r.mois, v)} data-testid={`ecart-save-${r.mois}`}><Save className="h-4 w-4" /></Button>}</td>
    </tr>
  );
};

const YearTable = ({ y, editable, onSave }) => (
  <div className="mb-6" data-testid={`ecarts-annee-${y.annee}`}>
    <p className="font-semibold mb-2">{y.mois ? `${MOIS[y.mois - 1]} ` : "Exercice "}{y.annee} <span className="text-xs font-normal text-muted-foreground">({y.mois_declares}/{y.rows.length} mois déclarés saisis)</span></p>
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[1300px]">
        <thead><tr className="text-left text-muted-foreground border-b">
          <th className="py-2 pr-2 font-medium">Mois</th>
          {K.map(([k, l]) => [`${l} facturé`, `${l} déclaré`, `Écart ${l}`].map((h) => <th key={`${k}${h}`} className="py-2 pr-2 font-medium text-right">{h}</th>))}
          <th />
        </tr></thead>
        <tbody className="divide-y">
          {y.rows.map((r) => <EcartRow key={`${y.annee}-${r.mois}-${r.ca_declare}-${r.tva_declare}-${r.timbre_declare}`} r={r} editable={editable} onSave={(m, v) => onSave(y.annee, m, v)} />)}
          <tr className="font-bold border-t-2 border-primary/30" data-testid={`ecarts-total-${y.annee}`}>
            <td className="py-2">TOTAL</td>
            {K.map(([k]) => ["facture", "declare", "ecart"].map((t) => <td key={`${k}${t}`} className={`py-2 pr-2 tabular text-right ${t === "ecart" ? cls(y.total[`${k}_ecart`]) : ""}`}>{fmtDA(y.total[`${k}_${t}`])}</td>))}
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

export const G50Ecarts = ({ sid, socName, editable }) => {
  const [data, setData] = useState(null);
  const [fa, setFa] = useState("");
  const [fm, setFm] = useState("");
  const q = new URLSearchParams({ ...(fa && { annee: fa }), ...(fa && fm && { mois: fm }) }).toString();
  const load = () => api.get(`/g50-decl/ecarts/${sid}${q ? `?${q}` : ""}`).then((r) => setData(r.data)).catch(() => setData([]));
  useEffect(() => { if (sid) { setData(null); load(); } }, [sid, fa, fm]); // eslint-disable-line
  const slug = (socName || "").replace(/\s+/g, "-") + (fa ? `-${fa}` : "") + (fa && fm ? `-${String(fm).padStart(2, "0")}` : "");
  const num = (x) => (x === "" || x === null ? null : Number(x));

  const onSave = async (annee, mois, v) => {
    try {
      const { data: res } = await api.put(`/g50-decl/${sid}/${annee}/${mois}/declare`, { ca_declare: num(v.ca), tva_declaree: num(v.tva), timbre_declare: num(v.timbre) });
      if (res.alerte) toast.warning(res.alerte, { duration: 8000 });
      else toast.success("Montants déclarés enregistrés");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    }
  };

  return (
    <div data-testid="g50-ecarts">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <select className={SEL} value={fa} onChange={(e) => { setFa(e.target.value); if (!e.target.value) setFm(""); }} data-testid="ecarts-annee-filter">
            <option value="">Toutes les années</option>
            {[2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className={SEL} value={fm} disabled={!fa} onChange={(e) => setFm(e.target.value)} data-testid="ecarts-mois-filter">
            <option value="">Toute l'année</option>
            {MOIS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <p className="text-xs text-muted-foreground">Écart = facturé − déclaré. Saisissez les montants de vos G50 déposés ; en rouge, tout écart (alerte automatique).</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => downloadPdf(`/g50-decl/ecarts/${sid}/pdf${q ? `?${q}` : ""}`, `ecarts-G50-${slug}`)} data-testid="ecarts-pdf-btn"><FileDown className="h-4 w-4" /> PDF</Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => downloadExcel(`/g50-decl/ecarts/${sid}/excel${q ? `?${q}` : ""}`, `ecarts-G50-${slug}`)} data-testid="ecarts-excel-btn"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
        </div>
      </div>
      {data === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : data.map((y) => <YearTable key={y.annee} y={y} editable={editable} onSave={onSave} />)}
    </div>
  );
};
